import React, { useState, useRef, useEffect } from 'react';
import { Info, X, Folder } from 'lucide-react';

interface CategoryInfo {
  name: string;
  count: number;
  icon?: string;
}

interface InfoPopoverProps {
  title: string;
  categories?: CategoryInfo[];
  totalPages?: number;
  score?: number;
  description?: string;
  children?: React.ReactNode;
  triggerClassName?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export const InfoPopover: React.FC<InfoPopoverProps> = ({
  title,
  categories = [],
  totalPages,
  score,
  description,
  children,
  triggerClassName = '',
  position = 'top',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(position);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Auto-detect best position based on available space
  useEffect(() => {
    if (!isOpen || !triggerRef.current || !popoverRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const popover = popoverRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let bestPosition = position;

    // Check vertical positions
    if (position === 'top' || position === 'bottom') {
      const spaceAbove = trigger.top;
      const spaceBelow = viewport.height - trigger.bottom;
      const popoverHeight = popover.height || 400; // Estimate if not rendered yet

      if (position === 'top' && spaceAbove < popoverHeight && spaceBelow > spaceAbove) {
        bestPosition = 'bottom';
      } else if (position === 'bottom' && spaceBelow < popoverHeight && spaceAbove > spaceBelow) {
        bestPosition = 'top';
      }
    }

    // Check horizontal positions
    if (position === 'left' || position === 'right') {
      const spaceLeft = trigger.left;
      const spaceRight = viewport.width - trigger.right;
      const popoverWidth = popover.width || 320;

      if (position === 'left' && spaceLeft < popoverWidth && spaceRight > spaceLeft) {
        bestPosition = 'right';
      } else if (position === 'right' && spaceRight < popoverWidth && spaceLeft > spaceRight) {
        bestPosition = 'left';
      }
    }

    setActualPosition(bestPosition);
  }, [isOpen, position]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const getPositionClasses = () => {
    switch (actualPosition) {
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 -translate-y-1/2 ml-2';
      default: // top
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`
          p-1.5 rounded-md transition-all
          ${isOpen ? 'bg-brand-100 text-brand-500' : 'text-gray-400 hover:text-brand-500 hover:bg-gray-100'}
          ${triggerClassName}
        `}
        aria-label="More information"
        aria-expanded={isOpen}
      >
        <Info className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className={`
            absolute z-50 ${getPositionClasses()}
            bg-white rounded-xl border border-gray-200 shadow-2xl
            w-80 max-w-sm
            animate-in fade-in zoom-in-95 duration-200
          `}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-4 border-b border-gray-100">
            <div className="flex-1 pr-4">
              <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
              {description && (
                <p className="text-xs text-gray-600 mt-1">{description}</p>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700 flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Stats */}
          {(totalPages !== undefined || score !== undefined) && (
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-4 text-sm">
                {totalPages !== undefined && (
                  <div>
                    <span className="text-gray-600">Pages:</span>{' '}
                    <span className="font-semibold text-gray-900">{totalPages}</span>
                  </div>
                )}
                {score !== undefined && (
                  <div>
                    <span className="text-gray-600">Match:</span>{' '}
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-brand-100 text-brand-700">
                      {score}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom children content */}
          {children}

          {/* Categories list */}
          {categories.length > 0 && (
            <div className="p-4">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Categories ({categories.length})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                {categories.map((category, index) => (
                  <div
                    key={`${category.name}-${index}`}
                    className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 hover:border-brand-300 hover:bg-brand-50 transition-all"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      {category.icon ? (
                        <span className="text-lg flex-shrink-0">{category.icon}</span>
                      ) : (
                        <Folder className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {category.name}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-brand-500 bg-brand-50 px-2 py-1 rounded ml-2 flex-shrink-0">
                      {category.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {categories.length === 0 && !children && (
            <div className="p-8 text-center">
              <Folder className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No additional information</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InfoPopover;
